import React, { useEffect } from "react"

import * as Sentry from "@sentry/react"

import { API_ORIGIN } from "@/lib/constants"
import { useCurrentUserQuery } from "@/src/graphql/public-generated-rq"
import { identifyUser } from "@/src/lib/posthog"

import { AuthContext, useAuth } from "./AuthContext"

export { useAuth } from "./AuthContext"

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data } = useCurrentUserQuery()

  const user = data?.currentUser

  const isAuthenticated = user?.isAuthenticated
  const isAdmin = user?.isAdmin
  const pid = user?.pid
  const email = user?.email
  const name = user?.name
  const isImpersonated = user?.isImpersonated
  const org = user?.org

  useEffect(() => {
    if (!isAuthenticated || !pid) return

    identifyUser({
      pid,
      email,
      name,
      isAdmin,
      isImpersonated,
      org,
    })
    Sentry.setUser({
      id: pid,
      email: email ?? undefined,
      username: name ?? undefined,
    })
  }, [email, isAdmin, isAuthenticated, isImpersonated, name, org, pid])

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isAdmin, pid, email, name, isImpersonated }}
    >
      {children}
    </AuthContext.Provider>
  )
}

/*
 * Render children only when the user is confirmed authenticated.
 * Redirect to /login if the user is not authenticated.
 */
export function AuthRequired({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (isAuthenticated === false) {
      const next = window.location
      location.href =
        API_ORIGIN +
        "/login?next=" +
        encodeURIComponent(next.pathname + next.search + next.hash)
    }
  }, [isAuthenticated])

  if (isAuthenticated === true) {
    return <>{children}</>
  }
  return null
}
